import { z } from 'zod';
import { skillCapabilitiesSchema } from './capabilities';
import { offerOwnershipErrors, offersSchema } from './offers';

const scalar = z.union([z.string(), z.number().finite(), z.boolean()]);
const predicate = z
  .object({
    field: z.string().regex(/^[a-zA-Z][a-zA-Z0-9.]*$/),
    op: z.enum(['eq', 'in', 'gte', 'exists']),
    value: z.union([scalar, z.array(scalar).min(1)]).optional(),
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.op !== 'exists' && rule.value === undefined)
      ctx.addIssue({ code: 'custom', message: 'Comparison requires a value' });
    if (rule.op === 'gte' && typeof rule.value !== 'number')
      ctx.addIssue({ code: 'custom', message: 'gte requires a numeric value' });
    if (rule.op === 'in' && !Array.isArray(rule.value))
      ctx.addIssue({ code: 'custom', message: 'in requires a list' });
  });

const ianaTimeZone = z
  .string()
  .min(1)
  .refine((timezone) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: timezone });
      return true;
    } catch {
      return false;
    }
  }, 'A valid IANA timezone is required');

export const skillPolicySchema = z
  .object({
    version: z.literal(1),
    id: z
      .string()
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
      .max(64),
    kind: z.enum(['discovery', 'source', 'analysis']),
    /** Bump for incompatible collection inputs, data, or evidence semantics. Omission fails closed to the release digest. */
    /** The specific collection contract that can read pre-versioned checkpoints. */
    legacyCollectionVersion: z
      .string()
      .regex(/^[a-zA-Z0-9._-]{1,80}$/)
      .optional(),
    collectionVersion: z
      .string()
      .regex(/^[a-zA-Z0-9._-]{1,80}$/)
      .optional(),
    displayName: z.string().trim().min(1).max(120).optional(),
    audiences: z.array(z.enum(['public-preview', 'saved-property'])).min(1),
    readiness: z
      .object({
        after: z.enum(['inputs', 'source-record', 'applicable-sources', 'accepted-changes']),
        documentContents: z.boolean(),
        exactInputVersions: z.literal(true),
      })
      .strict(),
    entrypoint: z
      .string()
      .regex(/^scripts\/[a-z0-9-]+\.ts$/)
      .optional(),
    runtime: z
      .string()
      .regex(/^scripts\/[a-z0-9-]+\.ts$/)
      .optional(),
    analysisEntrypoint: z
      .string()
      .regex(/^scripts\/[a-z0-9-]+\.ts$/)
      .optional(),
    /** What each detail the skill emits means to an owner; see src/issues/field-meanings.ts. */
    fieldMeanings: z
      .string()
      .regex(/^scripts\/[a-z0-9-]+\.ts$/)
      .optional(),
    applicability: z
      .object({
        all: z.array(predicate).min(1),
        reason: z.string().min(1),
      })
      .strict(),
    dependencies: z.array(
      z
        .object({
          skill: z.string(),
          required: z.boolean(),
        })
        .strict(),
    ),
    monitoring: z
      .object({
        triggers: z
          .array(z.enum(['save', 'scheduled', 'facts-changed', 'sources-changed', 'manual']))
          .min(1),
        intervalSeconds: z
          .number()
          .int()
          .min(60)
          .max(366 * 86400),
        freshSeconds: z.number().int().min(0),
        maxStaleSeconds: z.number().int().min(0),
        retrySeconds: z.number().int().min(1),
        maxAttempts: z.number().int().min(1).max(8),
        timeoutSeconds: z.number().int().min(1).max(600),
        publication: z
          .object({
            timezone: ianaTimeZone,
            after: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
          })
          .strict()
          .optional(),
      })
      .strict()
      .refine((p) => p.maxStaleSeconds >= p.freshSeconds, 'Stale retention must cover freshness'),
    outputs: z.array(z.string()).min(1),
    /**
     * How a person counts what this source returns, by record category: an
     * openable record by open and closed, a filed one by its newest date. The
     * property page says what came back from these; see source-results.ts.
     */
    results: z
      .array(
        z
          .object({
            category: z.string().min(1).max(100),
            noun: z.tuple([z.string().trim().min(1).max(60), z.string().trim().min(1).max(60)]),
            count: z.enum(['open', 'newest']),
          })
          .strict(),
      )
      .min(1)
      .max(12)
      .optional(),
    capabilities: skillCapabilitiesSchema.optional(),
    /** Self-serve offers on this skill's own items; see offers.ts. */
    offers: offersSchema.optional(),
    /**
     * The local time zone of the properties this skill applies to. The platform
     * shows a property's dates and times, and judges its days, in the zone its
     * applicable skills declare; see property-time.ts.
     */
    timeZone: ianaTimeZone.optional(),
  })
  .strict()
  // Every caller resolves a property's zone from its address alone, so a skill
  // that declares one may only decide applicability by country, region and city.
  .refine(
    (policy) =>
      !policy.timeZone ||
      policy.applicability.all.every((condition) =>
        ['country', 'region', 'city'].includes(condition.field),
      ),
    'A skill declaring timeZone may only apply by country, region and city',
  )
  .superRefine((policy, ctx) => {
    for (const message of offerOwnershipErrors(policy.id, policy.offers ?? []))
      ctx.addIssue({ code: 'custom', message });
    if (policy.offers?.length && policy.kind === 'discovery')
      ctx.addIssue({ code: 'custom', message: 'Discovery skills produce no items to offer on' });
    if (policy.kind !== 'analysis' && !policy.entrypoint)
      ctx.addIssue({ code: 'custom', message: 'Collector entrypoint required' });
    if (policy.kind !== 'analysis' && !policy.runtime)
      ctx.addIssue({ code: 'custom', message: 'Runtime result adapter required' });
    if (policy.kind === 'analysis' && !policy.analysisEntrypoint)
      ctx.addIssue({ code: 'custom', message: 'Analysis entrypoint required' });
    if (policy.kind !== 'analysis' && policy.readiness.after !== 'inputs')
      ctx.addIssue({ code: 'custom', message: 'Collectors run after their declared inputs' });
    if (policy.kind === 'analysis' && policy.readiness.after === 'inputs')
      ctx.addIssue({ code: 'custom', message: 'Analysis must declare its publication barrier' });
  });

export type SkillPolicy = z.infer<typeof skillPolicySchema>;
export type PropertyFacts = Record<string, unknown>;
export type Skill = { policy: SkillPolicy; instructions: string; digest: string };
export type Applicability = {
  status: 'applicable' | 'not-applicable' | 'needs-information';
  reason: string;
  evidence: Array<{ field: string; value: unknown }>;
  missing: string[];
};

function fieldValue(facts: PropertyFacts, path: string): unknown {
  let value: unknown = facts;
  for (const key of path.split('.')) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') return undefined;
    if (!value || typeof value !== 'object' || !Object.hasOwn(value, key)) return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

export function applicability(policy: SkillPolicy, facts: PropertyFacts): Applicability {
  const evidence: Applicability['evidence'] = [];
  const missing: string[] = [];
  let mismatch = false;
  for (const condition of policy.applicability.all) {
    const value = fieldValue(facts, condition.field);
    if (value === undefined || value === null || value === '') {
      missing.push(condition.field);
      continue;
    }
    evidence.push({ field: condition.field, value });
    if (condition.op === 'eq' && value !== condition.value) mismatch = true;
    if (
      condition.op === 'in' &&
      (!Array.isArray(condition.value) || !condition.value.includes(value as string))
    )
      mismatch = true;
    if (
      condition.op === 'gte' &&
      (typeof value !== 'number' || !Number.isFinite(value) || value < Number(condition.value))
    )
      mismatch = true;
  }
  return {
    status: mismatch ? 'not-applicable' : missing.length ? 'needs-information' : 'applicable',
    reason: policy.applicability.reason,
    evidence,
    missing,
  };
}

export function validateRegistry(skills: Skill[]) {
  const ids = new Set<string>();
  for (const { policy } of skills) {
    skillPolicySchema.parse(policy);
    if (ids.has(policy.id)) throw new Error(`Duplicate skill: ${policy.id}`);
    ids.add(policy.id);
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const ordered: Skill[] = [];
  const byId = new Map(skills.map((s) => [s.policy.id, s]));
  function visit(id: string) {
    if (visiting.has(id)) throw new Error(`Dependency cycle: ${id}`);
    if (visited.has(id)) return;
    const skill = byId.get(id);
    if (!skill) throw new Error(`Missing dependency: ${id}`);
    visiting.add(id);
    for (const dependency of skill.policy.dependencies) visit(dependency.skill);
    visiting.delete(id);
    visited.add(id);
    ordered.push(skill);
  }
  const stage = { discovery: 0, source: 1, analysis: 2 };
  for (const id of [...ids].sort(
    (a, b) =>
      stage[byId.get(a)!.policy.kind] - stage[byId.get(b)!.policy.kind] || a.localeCompare(b),
  ))
    visit(id);
  return ordered;
}

function publicationKey(
  date: Date,
  publication: NonNullable<SkillPolicy['monitoring']['publication']>,
) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: publication.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const key = `${values.year}-${values.month}-${values.day}`;
  if (`${values.hour}:${values.minute}` >= publication.after) return key;
  return new Date(Date.parse(`${key}T12:00:00Z`) - 86400000).toISOString().slice(0, 10);
}

export function sourceFreshness(policy: SkillPolicy, completedAt: string | null, now: Date) {
  if (!Number.isFinite(now.getTime())) throw new Error('Invalid scheduling time');
  const completed = completedAt ? new Date(completedAt) : null;
  if (!completed || !Number.isFinite(completed.getTime()) || completed > now)
    return { fresh: false, usableStale: false };
  const age = (now.getTime() - completed.getTime()) / 1000;
  const publication = policy.monitoring.publication;
  const samePublication =
    !publication || publicationKey(now, publication) === publicationKey(completed, publication);
  return {
    fresh: age < policy.monitoring.freshSeconds && samePublication,
    usableStale: age < policy.monitoring.maxStaleSeconds,
  };
}

export function nextCheck(policy: SkillPolicy, completedAt: string, failed = false) {
  const time = Date.parse(completedAt);
  if (!Number.isFinite(time)) throw new Error('Invalid completion time');
  let next =
    time + 1000 * (failed ? policy.monitoring.retrySeconds : policy.monitoring.intervalSeconds);
  const publication = policy.monitoring.publication;
  if (!failed && publication) {
    const key = publicationKey(new Date(time), publication);
    const nextDay = new Date(Date.parse(`${key}T12:00:00Z`) + 86400000).toISOString().slice(0, 10);
    const wall = Date.parse(`${nextDay}T${publication.after}:00Z`);
    let instant = wall;
    for (let i = 0; i < 3; i++) {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-CA', {
          timeZone: publication.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        })
          .formatToParts(new Date(instant))
          .map((p) => [p.type, p.value]),
      );
      instant +=
        wall -
        Date.parse(
          `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}Z`,
        );
    }
    if (instant > time) next = Math.min(next, instant);
  }
  return new Date(next).toISOString();
}

export function planSkills(skills: Skill[], facts: PropertyFacts) {
  return validateRegistry(skills).map((skill) => ({
    ...skill,
    applicability: applicability(skill.policy, facts),
  }));
}
